import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Cookie, CheckCircle } from 'lucide-react';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 pt-32 pb-20">
      {/* Futuristic grid background */}
      <div className="fixed inset-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(to right, #1e293b 1px, transparent 1px),
            linear-gradient(to bottom, #1e293b 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl mb-6 shadow-xl shadow-blue-500/30"
          >
            <Cookie className="w-10 h-10 text-white" />
          </motion.div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-4">
            Cookie Policy
          </h1>
          
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: 80 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="h-1 bg-gradient-to-r from-blue-600 to-indigo-600 mx-auto rounded-full mb-4"
          />
          
          <p className="text-slate-600 text-lg">Last updated: March 25, 2026</p>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-slate-200 p-8 md:p-12"
        >
          <div className="prose prose-slate max-w-none">
            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 flex items-center">
                <Shield className="w-7 h-7 mr-3 text-blue-600" />
                What Are Cookies
              </h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                Cookies are small text files that are placed on your computer or mobile device when you visit our website. They are widely used to make websites work more efficiently and provide information to the owners of the site. TransactWorld uses cookies to enhance your browsing experience and provide personalized services.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">How We Use Cookies</h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                We use cookies for various purposes to improve your experience on our platform:
              </p>
              <div className="space-y-3">
                {[
                  'Essential Cookies: Required for the operation of our website and services',
                  'Performance Cookies: Help us understand how visitors interact with our website',
                  'Functionality Cookies: Enable enhanced functionality and personalization',
                  'Analytics Cookies: Help us improve our services by collecting anonymous usage data',
                  'Security Cookies: Authenticate users and prevent fraudulent use of login credentials'
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + idx * 0.1 }}
                    className="flex items-start space-x-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100"
                  >
                    <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </motion.div>
                ))}
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Third-Party Cookies</h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                In addition to our own cookies, we may use third-party cookies to report usage statistics of our services and to deliver advertisements. These third parties include analytics providers, payment processors, and advertising networks. We ensure that all third-party service providers comply with applicable data protection regulations.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Managing Cookies</h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                You have the right to decide whether to accept or reject cookies. Most web browsers automatically accept cookies, but you can usually modify your browser settings to decline cookies if you prefer. However, this may prevent you from taking full advantage of our website and services.
              </p>
              <p className="text-slate-700 leading-relaxed text-justify">
                To learn more about cookies and how to manage them, visit www.allaboutcookies.org or www.youronlinechoices.eu.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Cookie Duration</h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                We use both session cookies (which expire when you close your browser) and persistent cookies (which remain on your device until they expire or you delete them). The duration of persistent cookies varies depending on their purpose, but they are typically stored for a period ranging from a few days to several years.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Updates to This Policy</h2>
              <p className="text-slate-700 leading-relaxed text-justify">
                We may update this Cookie Policy from time to time to reflect changes in our practices or for operational, legal, or regulatory reasons. We encourage you to review this policy periodically. Your continued use of our website after any changes indicates your acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Contact Us</h2>
              <p className="text-slate-700 leading-relaxed text-justify">
                If you have any questions about our use of cookies or this Cookie Policy, please contact us at:
              </p>
              <div className="mt-4 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                <p className="text-slate-800 font-semibold mb-2">TransactWorld Limited</p>
                <p className="text-slate-700">Email: privacy@transactworld.com</p>
                <p className="text-slate-700">Phone: +44 20 3129 7109</p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CookiePolicy;